import React from 'react';
import { Link } from 'react-router-dom';
import { Radar, HelpCircle } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { AUTOPILOT_ENABLED } from '@/lib/featureFlags';
import { useAutopilot } from '@/features/autopilot/AutopilotContext';

// The single Autopilot control under the sidebar rail (its own control, not a
// nav item). The radar opens the Autopilot dashboard; the switch turns the
// guided cursor on/off; the "?" explains it. There is no separate rail icon.
export default function AutopilotToggle() {
  const { enabled, toggle } = useAutopilot();
  if (!AUTOPILOT_ENABLED) return null;
  const navy = 'rounded-lg px-3 py-1.5 text-sm font-medium bg-navy-900 text-white border-0 shadow-md';
  return (
    <TooltipProvider>
      <div className="flex items-center gap-1.5">
        {/* radar = open the Autopilot dashboard (replaces the old rail nav icon) */}
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Link
              to="/autopilot" aria-label="Open Autopilot"
              className={`transition-colors ${enabled ? 'text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <Radar className="w-5 h-5" />
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={12} className={navy}>Open Autopilot</TooltipContent>
        </Tooltip>

        {/* switch = guided cursor on/off (gray off, indigo on) */}
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Switch
              checked={enabled}
              onCheckedChange={toggle}
              aria-label={`Autopilot guide ${enabled ? 'on' : 'off'}`}
              style={{ backgroundColor: enabled ? '#4f46e5' : '#94a3b8' }}
            />
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={12} className={navy}>Guide {enabled ? 'on' : 'off'}</TooltipContent>
        </Tooltip>

        {/* "?" = explanation */}
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" aria-label="What is Autopilot?" className="text-slate-300 hover:text-slate-600 transition-colors">
              <HelpCircle className="w-4 h-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent side="right" sideOffset={12} className="w-72">
            <div className="text-[13px] font-semibold text-slate-900 mb-1">Autopilot</div>
            <p className="text-[13px] leading-relaxed text-slate-600">
              Autopilot watches your production plan, inventory, and suppliers. When something
              threatens a run, it surfaces the risk — what&apos;s at stake in dollars and the
              recommended fix — as a guided card on screen. The radar opens the full dashboard;
              this switch turns the on-screen guide on or off.
            </p>
          </PopoverContent>
        </Popover>
      </div>
    </TooltipProvider>
  );
}
